import { Card, CompartmentCard } from "@/src/components/cards";
import { MesureChartDrawer } from "../patients/mesuresChart";
import { USER } from "@/src/data/globalVariables";
import { FieldOption, FolderItem, Patient } from "@/src/data/allTypes";
import { useState } from "react";
import { InfiniteFetchableList } from "@/src/components/fetchableLists";
import moment from "moment";
import apiFetch from "@/src/utils/apiFetch";
import {
  getItemId,
  getUserId,
  getUserShortName,
  uid,
} from "@/src/utils/utils";
import { openConfirmDialog } from "@/src/components/dialogs";
import { openGrowthInputs } from "./growthInputsDrawer";
import { useQuery } from "@tanstack/react-query";
import { getStandardDeviation } from "@/src/data/pediatricsChartData";

function GrowthChartsCompartment({
  patient,
  parent,
}: {
  patient: Patient;
  parent: FolderItem | null;
}) {
  let {
    data: references,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["GrowthChartsCompartment", getUserId()],
    queryFn: () => {
      let frequent = USER?.get()?.speciality?.frequentReferences?.filter(
        (reference) => reference?.category == "mesure"
      );

      if (frequent?.length) return frequent;
      return apiFetch("references", "loadMany", {
        userId: getUserId(),
        categoriesIds: ["mesure"],
      });
    },
  });

  let weightReference = references?.find((ref) =>
    ref?.name?.toLowerCase().includes("poids")
  );
  let heightReference = references?.find((ref) =>
    ref?.name?.toLowerCase().includes("taille")
  );
  let headCircumferenceReference = references?.find((ref) =>
    ref?.name?.toLowerCase().includes("périmètre cranien")
  );
  let bmiReference = references?.find((ref) =>
    ref?.name?.toLowerCase().includes("bmi")
  );

  return (
    <div className="mc1 c2 gap20">
      <div className=" c1 gap20">
        {heightReference != null && (
          <MesureChartDrawer
            parent={null}
            patient={patient}
            reference={heightReference}
            showMesuresList={false}
          />
        )}
        {weightReference != null && (
          <MesureChartDrawer
            parent={null}
            patient={patient}
            reference={weightReference}
            showMesuresList={false}
          />
        )}
      </div>
      <div className="c1 gap20">
        {headCircumferenceReference != null && (
          <MesureChartDrawer
            parent={null}
            patient={patient}
            reference={headCircumferenceReference}
            showMesuresList={false}
          />
        )}
        {bmiReference != null && (
          <MesureChartDrawer
            parent={null}
            patient={patient}
            reference={bmiReference}
            showMesuresList={false}
          />
        )}
      </div>
      <div className="double msingle">
        <AllGrowthMesuresList
          patient={patient}
          parent={parent}
          referenceIds={[
            getItemId(weightReference),
            getItemId(heightReference),
            getItemId(headCircumferenceReference),
            getItemId(bmiReference),
          ].filter((id) => id != null)}
        ></AllGrowthMesuresList>
      </div>
    </div>
  );
}

function AllGrowthMesuresList({
  patient,
  parent,
  referenceIds,
}: {
  patient: Patient;
  parent: FolderItem | null;
  referenceIds: string[];
}) {
  const [reload, setReload] = useState(0);

  function getValue(
    value: any,
    options: (FieldOption | null | undefined)[]
  ): any {
    if (Array.isArray(value)) return value.map((v) => getValue(v, options));
    let val = value?.id || value;
    let option = options?.find((option) => option?.id === val);
    return option?.name || val;
  }

  if (referenceIds.length == 0) return null;

  return (
    <CompartmentCard
      title="Mesures"
      color="red"
      onClick={async () => {
        await openGrowthInputs({
          patient,
          parent,
          onConfirm: (mesures) => {
            setReload((prev) => prev + 1);
          },
        });
      }}
    >
      <InfiniteFetchableList
        queryKey={[
          "MesureChartDrawer",
          patient.id,
          JSON.stringify(referenceIds),
          reload,
        ]}
        getFunction={(limit, offset, searchKey) => {
          return apiFetch("patientMesures", "loadPatientMesures", {
            patientId: getItemId(patient),
            referencesIds: referenceIds,
            limit: limit,
            offset: offset,
            sortObj: {
              _date: "DESC",
            },
          });
        }}
      >
        {(data) => {
          return (
            <div className="c1 gap10">
              {data?.map((mesure) => {
                return (
                  <Card
                    key={mesure.id}
                    color="red"
                    className="empty"
                    title={mesure.reference?.name}
                    onDelete={async () => {
                      await openConfirmDialog({
                        title: "Supprimer la mesure",
                        content:
                          "Êtes-vous sûr de vouloir supprimer cette mesure ?",
                        onConfirm: async () => {
                          await apiFetch("general", "deleteAny", {
                            id: mesure.id,
                            table: "refAnswers",
                          });
                          setReload((prev) => prev + 1);
                        },
                      });
                    }}
                  >
                    <div className="grid c1 ">
                      <div className="c1 gap5">
                        {mesure.fieldAnswers?.map((answer) => {
                          return (
                            <div key={uid()} className="bold">
                              {getValue(
                                answer?.value,
                                answer?.field?.options || []
                              )}{" "}
                              {answer?.field?.unit?.name || ""} (
                              {getStandardDeviation({
                                value: parseFloat(
                                  answer?.value?.toString() || "0"
                                ),
                                reference: mesure.reference!,
                                date: mesure._date,
                                patient: patient,
                              })}
                              )
                            </div>
                          );
                        })}{" "}
                      </div>
                      <div className=" black">
                        {moment(mesure._date).format("LL")}{" "}
                        <span className="normal gray">
                          {moment(mesure._date).format("à HH:mm")} par{" "}
                          {getUserShortName(mesure.creator)}
                        </span>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          );
        }}
      </InfiniteFetchableList>
    </CompartmentCard>
  );
}

export default GrowthChartsCompartment;
